import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ExternalLinkIcon, PencilIcon } from "lucide-react";
import { EditClientModulesDialog } from "@/components/EditClientModulesDialog";
import { formatDate } from "@/lib/format";

type ClientModule = components["schemas"]["ClientModuleResponse"];

const statusVariant: Record<string, "default" | "outline" | "secondary"> = {
  Active: "default",
  Preview: "outline",
  Deprecated: "secondary",
};

export function ClientModulesCard({ clientId }: { clientId: string }) {
  const [modules, setModules] = useState<ClientModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchModules() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/clients/{clientId}/modules", {
        params: { path: { clientId } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load modules.");
      } else {
        setModules(data ?? []);
      }
      setLoading(false);
    }
    fetchModules();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Areas first (no parent), then their features, each area block ordered by key.
  const sorted = [...modules].sort((a, b) => {
    const areaA = a.parentKey ?? a.key;
    const areaB = b.parentKey ?? b.key;
    if (areaA !== areaB) return areaA.localeCompare(areaB);
    if (!a.parentKey) return -1;
    if (!b.parentKey) return 1;
    return a.key.localeCompare(b.key);
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Modules</CardTitle>
            <p className="text-muted-foreground mt-1 text-sm">
              Entitlements this client holds. Feature access is resolved from this list per request.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
            <PencilIcon className="size-4" />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : modules.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              This client holds no modules. Use Edit to grant entitlements.
            </p>
          ) : (
            <ul className="divide-y">
              {sorted.map((mod) => (
                <li
                  key={mod.key}
                  className={mod.parentKey ? "flex items-center justify-between gap-4 py-2.5 pl-6" : "flex items-center justify-between gap-4 py-2.5"}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{mod.displayName}</span>
                      <code className="text-muted-foreground text-xs">{mod.key}</code>
                      {mod.status && mod.status !== "Active" && (
                        <Badge variant={statusVariant[mod.status] ?? "outline"} className="text-xs">
                          {mod.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-right">
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                      {mod.grantedAt ? `Granted ${formatDate(mod.grantedAt)}` : "-"}
                    </span>
                    {mod.grantedBy ? (
                      <Link to={`/users/${mod.grantedBy}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {mod.grantedBy.slice(0, 8)}...
                          <ExternalLinkIcon className="ml-1 size-3" />
                        </Badge>
                      </Link>
                    ) : (
                      // Not "System": a null granter may be the admin key or the backfill; only the audit log can tell.
                      <span className="text-muted-foreground text-xs">Not recorded</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <EditClientModulesDialog
        clientId={clientId}
        heldModules={modules}
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdated={setModules}
      />
    </>
  );
}
